import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AssociationManager } from '../associations/AssociationManager';
import { User } from '@/types/user.types';
import { Device, DeviceAccessType } from '@/types/device.types';
import { deviceService } from '@/services/api/services/deviceService';
import { userService } from '@/services/api/services/userService';
import { associationService } from '@/services/api/services/associationService';
import { AssociationWithDetailsResponse } from '@/types/association.types';

interface UserDeviceManagerProps {
  userId: string;
}

export const UserDeviceManager: React.FC<UserDeviceManagerProps> = ({ userId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [allDevices, setAllDevices] = useState<Device[]>([]);
  const [associations, setAssociations] = useState<
    Array<{ deviceId: string; typeOfAccess: DeviceAccessType }>
  >([]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch data in parallel
      const [userResponse, devicesResponse, associationsResponse] = await Promise.all([
        userService.getById(userId),
        deviceService.getAllForAssociations(),
        associationService.getUserAssociationsWithDevices(userId),
      ]);

      if (!userResponse.success) {
        throw new Error(userResponse.error.message || 'Failed to fetch user');
      }

      if (!devicesResponse.success) {
        throw new Error(devicesResponse.error.message || 'Failed to fetch devices');
      }

      if (!associationsResponse.success) {
        throw new Error(associationsResponse.error.message || 'Failed to fetch associations');
      }

      setUser(userResponse.data);
      setAllDevices(devicesResponse.data);
      setAssociations(
        associationsResponse.data.map((assoc: AssociationWithDetailsResponse) => ({
          deviceId: assoc.deviceId,
          typeOfAccess: assoc.typeOfAccess as DeviceAccessType,
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      await fetchData();
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-white p-8">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-brand-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 text-red-600 p-4 rounded-md">
            {error || t('errors.userNotFound')}
          </div>
          <button
            onClick={() => navigate('/users')}
            className="mt-4 bg-brand-white text-brand-orange border border-brand-orange hover:bg-brand-orange hover:text-brand-white px-4 py-2 rounded-md transition-colors duration-200"
          >
            {t('common.goBack')}
          </button>
        </div>
      </div>
    );
  }

  const currentAssociations = associations.map((access) => {
    const device = allDevices.find((d) => d.id === access.deviceId);
    return {
      id: access.deviceId,
      name: device?.serialNumber || access.deviceId,
      typeOfAccess: access.typeOfAccess,
    };
  });

  const availableEntities = allDevices
    .filter((device) => !associations.some((assoc) => assoc.deviceId === device.id))
    .map((device) => ({
      id: device.id || '',
      name: device.serialNumber || '',
      description: device.description || device.alias || '',
    }));

  const entityDetails = {
    id: user?.id || '',
    name: user
      ? `${user.personalInfo?.name || ''} ${user.personalInfo?.surname1 || ''}`.trim()
      : '',
    role: user?.role || '',
  };

  return (
    <div className="min-h-screen bg-brand-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-brand-gray-dark">
            {t('pages.users.manageDevices')}
          </h1>
          <div className="mt-2">
            {/* TODO: Traducir */}
            <span className="text-brand-gray-dark font-medium">Nombre de usuario: </span>
            <span className="text-brand-orange">{user?.credentials?.username}</span>
            {user?.personalInfo?.name && (
              <>
                <br />
                <span className="text-brand-gray-dark font-medium">Nombre y apellido: </span>
                <span className="text-brand-orange">
                  {`${user.personalInfo?.name || ''}, ${user.personalInfo?.surname1 || ''}`}
                </span>
              </>
            )}
          </div>
        </div>
        {/* TODO: Traducir todos los textos */}
        <AssociationManager
          entity={entityDetails}
          entityType="user"
          currentAssociations={currentAssociations.map((assoc) => ({
            ...assoc,
            displayInfo: {
              mainLabel: 'Número de serie:',
              mainValue: assoc.name,
              subLabel: 'Alias del dispositivo:',
              subValue: allDevices.find((d) => d.id === assoc.id)?.alias || '',
            },
          }))}
          availableEntities={availableEntities.map((entity) => ({
            ...entity,
            displayInfo: {
              mainLabel: 'Número de serie:',
              mainValue: entity.name,
              subLabel: 'Alias del dispositivo:',
              subValue: entity.description || '',
            },
          }))}
          onCancel={() => navigate('/users')}
          onAssociationChange={async (type, associationId) => {
            console.log(`Association ${type}:`, associationId);
            // Refresh data after association changes to update available entities
            await fetchData();
          }}
        />
      </div>
    </div>
  );
};
